/**
 * Quadro "Importar atividades": área de soltar arquivos. Ao enviar, abre uma janela temporária com o progresso
 * de cada arquivo (envio → leitura → pronto) e, no fim, as opções de ver a atividade ou atribuí-la a um treino
 * da planilha. Não há histórico de importações na tela: só as atividades realizadas (lista da página).
 */
import { useRef, useState, type DragEvent } from "react";
import { ACCEPTED_ACTIVITY_FILES } from "../lib/activities";
import { ImportProgressModal, type UploadEntry } from "./ImportProgress";
import { Button } from "./ui";
import { t, tj } from "../i18n";

export function ImportPanel({
  orgId,
  uploadPath,
  activityHref,
  title = t("Importar atividades"),
  onImported,
}: {
  orgId: string;
  uploadPath: string;
  activityHref: (id: string) => string;
  title?: string;
  onImported?: () => void;
}) {
  const [files, setFiles] = useState<File[] | null>(null);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const start = (list: FileList | File[]) => {
    const arr = Array.from(list);
    if (arr.length) setFiles(arr);
    if (input.current) input.current.value = "";
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (e.dataTransfer.files.length) start(e.dataTransfer.files);
  };

  return (
    <section className="panel" aria-labelledby="importar">
      <div className="panel-head">
        <h2 id="importar">{title}</h2>
      </div>
      <div className="panel-body stack">
        <div
          className={`dropzone${over ? " is-over" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
        >
          <p>
            {tj("{drag} ou escolha no aparelho.", { drag: <strong>{t("Arraste os arquivos do relógio aqui")}</strong> })}
          </p>
          <p className="muted">
            {t("FIT, TCX ou GPX (também .gz e o .zip exportado pelo Garmin Connect) e histórico em CSV ou XLSX. Até 25 MB por arquivo.")}
          </p>
          <input
            ref={input}
            id="import-file"
            type="file"
            multiple
            accept={ACCEPTED_ACTIVITY_FILES}
            className="visually-hidden"
            onChange={(e) => e.target.files && start(e.target.files)}
          />
          <label htmlFor="import-file" className="btn btn-primary">
            {t("Escolher arquivos")}
          </label>
        </div>
      </div>
      {files && (
        <ImportProgressModal
          orgId={orgId}
          uploadPath={uploadPath}
          files={files}
          activityHref={activityHref}
          onClose={() => setFiles(null)}
          onChanged={(entries: UploadEntry[]) => entries.some((e) => e.state === "done") && onImported?.()}
        />
      )}
    </section>
  );
}

// (reexport para telas que ainda importam o botão)
export { Button };
